/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import {Moment} from 'moment';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import useMedia from '../../hooks/useMedia';
import {MaterialBackIcon} from '../../icons/MaterialBackIcon';
import Hidden from '../../layout/Hidden';
import {dlsColor} from '../../theme/theme';
import {useThemeMode} from '../../theme/useThemeContext';
import {DateSelectorValues, DLSTheme, LastFilteredDateProps} from '../../types/types';
import Breadcrumb from '../breadcrumbs/Breadcrumbs';
import {defineBreadcrumbs} from '../breadcrumbs/BreadcrumbUtils';
import {DateFilter} from '../filters/DateFilter';
import FilterMegaMenu from './FilterMegaMenu';
import {getDateRangeFromValue} from './MenuUtils';
import MobileActionContent from './MobileActionContent';

export type RadioMapType = {
  [key in DateSelectorValues]: {
    label: string;
    daysFromToday?: number;
    icon?: ReactNode;
    onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  }
}

export interface DateFilterMegaMenuProps {
  elementId?: string;
  onSubmit: (
    key: string,
    val: {
      fromDate: Moment;
      toDate: Moment;
    },
    selectedRadio?: string
  ) => void;
  loading?: boolean;
  totalAssets?: number;
  minMenuWidth?: string;
  menuLeft?: string;
  menuRight?: string;
  menuHeight?: string;
  menuPaddingBottom?: string;
  selectorMargin?: string;
  setSelectedDateValue?: (value?: string) => void;
  selectedFilterValue?: DateSelectorValues;
  setSelectedFilterValue?: (value?: DateSelectorValues) => void;
  customDateMode?: boolean;
  custom?: boolean;
  dateRangeFromToday?: boolean;
  selectFieldMinWidth?: string;
  menuWidth?: string;
  lastFilteredDateProps?: LastFilteredDateProps;
  toggleView?: Function;
  includeLive?: boolean;
  isBackToMenu?: boolean;
  isDefaultValueOnMount?: boolean;
  includeBackIcon?: boolean;
  overrideDateTimePickerStyles?: GenericObject;
  dateTimePickerPlaceHolder?: string;
  disabled?: boolean;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  dateMenuSelector : {
    margin                         : ({selectorMargin}: DateFilterMegaMenuProps) => selectorMargin || 0,
    [theme.breakpoints.down('sm')] : {
      minWidth : 'auto',
      width    : '150px'
    }
  },
  menuTitle : {
    display    : 'flex',
    alignItems : 'center',
    '& h4'     : {
      marginLeft : '10px'
    }
  },
  leftIcon : {
    height : 'fit-content'
  },
  chevron : {
    fill       : dlsColor('grey3'),
    padding    : '8px 0 8px 8px',
    transition : 'transform 300ms ease-in-out',
    '&:hover'  : {
      transform  : 'translateX(-3px)',
      transition : 'transform 300ms ease-in-out'
    }
  },
  activeCrumb : {
    color : dlsColor('grey1')
  },
  inactiveCrumb : {
    color     : dlsColor('grey3'),
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  selectionItem : {
    width                          : '50%',
    height                         : 'fit-content',
    overflow                       : 'hidden',
    transition                     : 'transform 400ms ease-in-out',
    [theme.breakpoints.down('sm')] : {
      marginRight : '0px',
      width       : '50%'
    }
  },
  selectFieldValue : {
    color   : dlsColor('grey4'),
    padding : '0px 10px 0px 5px'
  },
  labelRoot : {
    display        : 'flex',
    gap            : '12px',
    justifyContent : 'center',
    alignItems     : 'center'
  }
}));

const SelectFieldValue = ({selectFieldValue}: {selectFieldValue: string}) => {
  const classes = useStyles({} as DateFilterMegaMenuProps);
  const {mode}  = useThemeMode();

  const style = useMemo(() => {
    const color = {
      [translate('common:time.live')] : 'caBlue'
    }[selectFieldValue];

    if (color) {
      return {fontSize: '18px', color: dlsColor(color)};
    }
  }, [selectFieldValue, mode]);
  
  return (
    <div className={classes.labelRoot}>
      <span>{translate('common:time.dates')}</span>
      <span className={classes.selectFieldValue} style={style}>{selectFieldValue}</span>
    </div>
  );
};

/**
 * The Date Filter Title is used in both desktop and mobile so it's been
 * abstracted here. Handles user backing out of custom date mode.
 *
 * ```typescript
 *
 * import {DateFilterTitle} from '@ion/dls/src/components/menus';
 *
 * <DateFilterTitle customDateMode={isCustomDate}
 *   onBackClick={onBackClick} />
 *
 * ```
 *
 */
export const DateFilterTitle = ({
  customDateMode,
  onBackClick,
  isNotificationFilter = false
}: {
  customDateMode: boolean;
  isNotificationFilter?: boolean;
  onBackClick: (e: MouseEvent<HTMLButtonElement>) => void;
}) => {
  const classes   = useStyles({} as DateFilterMegaMenuProps);
  const {isPhone} = useMedia();

  const crumbClasses = {
    activeCrumb   : classes.activeCrumb,
    inactiveCrumb : classes.inactiveCrumb
  };

  const crumb1 = {
    label   : ((isNotificationFilter && customDateMode) || !isNotificationFilter) ? translate('common:time.dates') : '',
    click   : true,
    onClick : function(e) {
      onBackClick(e);
    }
  };

  const crumbData = customDateMode
    ? defineBreadcrumbs({
      crumbs : [
        crumb1,
        {
          label : customDateMode && translate('common:time.custom'),
          click : false
        }
      ],
      classes : crumbClasses
    })
    : defineBreadcrumbs({
      crumbs  : [crumb1],
      classes : crumbClasses
    });

  const isEnableHeader = ((isNotificationFilter && customDateMode) || !isNotificationFilter);

  return (
    isEnableHeader && <div className={classes.menuTitle}>
      <Breadcrumb icon={customDateMode && <ChevronLeft className={classes.chevron} />}
        iconClick={onBackClick}
        height={isPhone && '45px'}>
        {crumbData}
      </Breadcrumb>
    </div>
  );
};

/**
 * Date Filter Menu that utilizes the FilterMegaMenu, specifically to choose
 * a date or date range and pass those values back to the component consumer
 * user clicks done. Note that mobile content is just that, content, so needs
 * to be wrapped with some sort of drawer to be displayed properly. Done this
 * way to allow the content to be nested and abstract it from being forced to the
 * root of a drawer.
 *
 * ```typescript
 *
 * import {DateFilterMegaMenu} from '@ion/dls/src/components/menus/DateFilterMegaMenu';
 *
 * <DateFilterMegaMenu loading={loading}
 *   onSubmit={handleSubmit} />
 *
 * ```
 *
 */
export const DateFilterMegaMenu = ({
  elementId,
  onSubmit,
  setSelectedDateValue,
  selectedFilterValue,
  setSelectedFilterValue,
  menuRight,
  menuHeight,
  menuPaddingBottom,
  selectorMargin,
  selectFieldMinWidth,
  menuWidth,
  lastFilteredDateProps,
  isDefaultValueOnMount,
  toggleView,
  disabled,
  menuLeft = '20.2%',
  totalAssets = 1,
  loading = false,
  custom = false,
  includeLive = false,
  dateRangeFromToday = false,
  minMenuWidth = '734px',
  isBackToMenu = false,
  includeBackIcon = true,
  overrideDateTimePickerStyles = null,
  dateTimePickerPlaceHolder = null
}: DateFilterMegaMenuProps) => {
  const {
    startDate,
    endDate,
    setFilterStartDate,
    setFilterEndDate
  } = lastFilteredDateProps || {};
  const [customDateMode, setCustomDateMode] = useState((
    selectedFilterValue === DateSelectorValues.Custom
  ));
  const [selectedDate, setSelectedDate]     = useState<Array<Moment>>(null);
  const [dateError, setDateError]           = useState<boolean>(false);
  const classes                             = useStyles({customDateMode, selectorMargin} as DateFilterMegaMenuProps);

  useEffect(() => {
    if (isDefaultValueOnMount) {
      setSelectedDate([startDate, endDate]);
    }
  }, [lastFilteredDateProps]);

  const defaultValue = useMemo(() => {
    if (includeLive) {
      return DateSelectorValues.Live;
    }
    return DateSelectorValues.Today;
  }, []);

  const [selectedRadio, setSelectedRadio]   = useState<DateSelectorValues>(
    selectedFilterValue ?? defaultValue
  );
  const [appliedFilter, setAppliedFilter]   = useState<DateSelectorValues>(
    selectedFilterValue ?? defaultValue
  );

  const isCustomValue = (value) => value === DateSelectorValues.Custom;
  const isDisabledForm = selectedRadio === DateSelectorValues.Custom &&
    (!selectedDate?.[0] || !selectedDate?.[1] || dateError);

  const radioMap: RadioMapType = useMemo(() => {
    let radioMap: RadioMapType = {
      [DateSelectorValues.Today] : {
        label         : translate('common:time.today'),
        daysFromToday : 0
      },
      [DateSelectorValues.Yesterday] : {
        label         : translate('common:time.yesterday'),
        daysFromToday : 1
      },
      [DateSelectorValues.LastSeven] : {
        label         : translate('common:time.lastDays', {number: 7}),
        daysFromToday : 7
      },
      [DateSelectorValues.LastThirty] : {
        label         : translate('common:time.lastDays', {number: 30}),
        daysFromToday : 30
      },
      [DateSelectorValues.Custom] : {
        label         : translate('common:time.custom'),
        daysFromToday : null,
        icon          : <ChevronRight />,
        onClick       : function() {
          setCustomDateMode(true);
          setSelectedRadio(DateSelectorValues.Custom);
        }
      }
    } as unknown as RadioMapType;
    if (includeLive) {
      radioMap = {
        [DateSelectorValues.Live] : {
          label         : translate('common:time.live'),
          daysFromToday : null
        },
        ...radioMap
      };
    }
    return radioMap;
  }, [includeLive]);

  const [selectFieldValue, setSelectFieldValue] = useState(
    custom
      ? radioMap[DateSelectorValues.Custom]?.label
      : radioMap[selectedRadio]?.label
  );

  useEffect(() => {
    // custom prop would be passed if date is coming from a query param
    // so we only need to set the radio to custom on mount
    if (custom) {
      setSelectedRadio(DateSelectorValues.Custom);
      setCustomDateMode(true);
    }
  }, [custom]);

  function onRadioChange(event: ChangeEvent<HTMLInputElement>) {
    const {value} = event.target as HTMLInputElement;
    setSelectedRadio(value as DateSelectorValues);
    if (isCustomValue(value)) setCustomDateMode(true);
  }

  function handleCancel() {
    setSelectedRadio(appliedFilter);
  }

  const setFilterDate = useCallback((selectedDate: Moment[]): void => {
    const [start, end] = selectedDate;
    setFilterStartDate?.(start);
    setFilterEndDate?.(end);
  }, [selectedDate]);

  function handleSubmit() {
    const selectedRadioLabel = radioMap[selectedRadio].label;
    setSelectFieldValue(selectedRadioLabel);
    setSelectedDateValue?.(selectedRadioLabel);
    const dateValue = getDateRangeFromValue(
      isCustomValue(selectedRadio) ? selectedDate : selectedRadio,
      dateRangeFromToday
    );
    selectedDate && setFilterDate(selectedDate);
    setAppliedFilter(selectedRadio);
    setSelectedFilterValue?.(selectedRadio);
    onSubmit?.('date', {...dateValue, selectedRadio});
  }

  const onCancel = useCallback(() => {
    toggleView?.();
  }, []);

  useEffect(() => {
    if (selectedFilterValue) {
      const selectedRadioLabel = radioMap[selectedFilterValue]?.label;
      setSelectedRadio(selectedFilterValue);
      setAppliedFilter(selectedFilterValue);
      setSelectFieldValue(selectedRadioLabel);
    }
    selectedFilterValue === DateSelectorValues.Custom && setCustomDateMode(true);
  }, [selectedFilterValue]);

  const onBackClick =  useCallback(() => {
    setCustomDateMode(false);
  }, []);

  useEffect(() => {
    if (isBackToMenu) {
      setCustomDateMode(false);
    }
  }, [isBackToMenu]);

  return (
    <>
      <Hidden show='desktop'>
        <div className={classes.dateMenuSelector}>
          <FilterMegaMenu elementId={elementId}
            loading={loading}
            minMenuWidth={minMenuWidth}
            menuWidth={menuWidth}
            menuPaddingBottom={menuPaddingBottom}
            menuHeight={menuHeight}
            selectFieldMinWidth={selectFieldMinWidth}
            menuLeft={menuLeft}
            menuRight={menuRight}
            selectFieldValue={<SelectFieldValue selectFieldValue={selectFieldValue} />}
            onMenuClose={handleCancel}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            disabled={disabled}
            primaryButtonDisabled={isDisabledForm}>
            <>
              <DateFilterTitle customDateMode={customDateMode}
                onBackClick={onBackClick} />
              <DateFilter customDateMode={customDateMode}
                onRadioChange={onRadioChange}
                selectedRadio={selectedRadio}
                setSelectedDate={setSelectedDate}
                radioMap={radioMap}
                setDateError={setDateError}
                lastFilteredDateProps={lastFilteredDateProps}
                totalAssets={totalAssets} />
            </>
          </FilterMegaMenu>
        </div>
      </Hidden>
      <Hidden show='mobile'>
        <MobileActionContent onSubmit={handleSubmit}
          primaryButtonDisabled={isDisabledForm}>
          <>
            {includeBackIcon && (
              <span onClick={onCancel}>
                <MaterialBackIcon />
              </span>
            )}
            <DateFilterTitle customDateMode={customDateMode}
              onBackClick={onBackClick} />
            <DateFilter customDateMode={customDateMode}
              onRadioChange={onRadioChange}
              selectedRadio={selectedRadio}
              setSelectedDate={setSelectedDate}
              radioMap={radioMap}
              setDateError={setDateError}
              lastFilteredDateProps={lastFilteredDateProps}
              totalAssets={totalAssets}
              overrideDateTimePickerStyles={overrideDateTimePickerStyles}
              dateTimePickerPlaceHolder={dateTimePickerPlaceHolder} />
          </>
        </MobileActionContent>
      </Hidden>
    </>
  );
};

export default DateFilterMegaMenu;
